import React, { useEffect, useState } from 'react';
import Modal from '../../../../common/Modal';
import Button from '../../../../common/Button';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../../../context/NotificationContext';
import { NotificationType, Position } from '../../../../../types/enums/notificationEnums';
import api from '../../../../../services/api';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import ResetUsageModal from './ResetUsageModal';
import { formatDateForInput, toMySQLDatetime } from '../../../../../utils/dateFormatter';
import { Coupon, CouponModalProps } from '../../../../../types/coupon';
import { validateCouponForm } from './formValidator';

// Initial state for a new coupon
const initialCouponState: Omit<Coupon, 'id' | 'used_count'> = {
  code: '',
  description: '',
  discount_type: 'fixed',
  discount_value: 0,
  min_cart_total: 0,
  start_date: formatDateForInput(new Date().toISOString()),
  end_date: formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()),
  usage_limit: 0,
  is_active: true
};

const CouponModal: React.FC<CouponModalProps> = ({
  modalOpen,
  setModalOpen,
  coupon,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState<Omit<Coupon, 'id' | 'used_count'>>(initialCouponState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [emptyInputs, setEmptyInputs] = useState<Record<string, boolean>>({});
  const [resetConfirmOpen, setResetConfirmOpen] = useState<boolean>(false);
  const [usedCount, setUsedCount] = useState<number>(0);

  // Reset form data when modal is opened/closed or coupon changes
  useEffect(() => {
    if (coupon) {
      // Edit mode - populate form with coupon data
      const { id, used_count, ...couponData } = coupon;
      setFormData({
        ...couponData,
        // Format dates for HTML datetime-local inputs
        start_date: formatDateForInput(coupon.start_date),
        end_date: formatDateForInput(coupon.end_date)
      });
      setUsedCount(used_count);
    } else {
      // Create mode - use initial state
      setFormData(initialCouponState);
      setUsedCount(0);
    }
    setErrors({});
    setEmptyInputs({}); // Reset empty inputs tracking
  }, [coupon, modalOpen]);

  // Close the modal
  const closeModal = () => {
    setModalOpen(false);
  };

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    let parsedValue: string | number | boolean = value;

    // Handle different input types
    if (type === 'number') {
      if (value === '') {
        // Track empty inputs to display them correctly
        setEmptyInputs(prev => ({ ...prev, [name]: true }));
        // Set the actual value to 0 but don't display it
        parsedValue = 0;
      } else {
        setEmptyInputs(prev => ({ ...prev, [name]: false }));
        parsedValue = parseFloat(value);
      }
    } else if (type === 'checkbox') {
      parsedValue = (e.target as HTMLInputElement).checked;
    }

    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate the form data
  const validateForm = (): boolean => {
    const { isValid, errors: newErrors } = validateCouponForm(formData, emptyInputs, t);
    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    // Prepare data for API submission
    const apiFormData = {
      ...formData,
      // Format dates for MySQL
      start_date: toMySQLDatetime(formData.start_date),
      end_date: toMySQLDatetime(formData.end_date),
      usage_limit: formData.usage_limit || 0
    };

    try {
      if (coupon) {
        // Update existing coupon
        await api.put(`/coupons/${coupon.id}`, apiFormData);
        showNotification(
          t('Coupon updated successfully'),
          NotificationType.Success,
          Position.TopRight
        );
      } else {
        // Create new coupon
        await api.post('/coupons', apiFormData);
        showNotification(
          t('Coupon created successfully'),
          NotificationType.Success,
          Position.TopRight
        );
      }

      onSuccess();
      closeModal();
    } catch (err) {
      console.error('Error saving coupon:', err);
      showNotification(
        t('Error occurred saving coupon. Please try again later.'),
        NotificationType.Error,
        Position.TopRight
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle resetting the used count
  const handleResetUsedCount = async () => {
    if (!coupon) return;

    setSubmitting(true);
    try {
      // Prepare update data with all the current coupon data but used_count set to 0
      const resetData = {
        ...formData,
        start_date: toMySQLDatetime(formData.start_date),
        end_date: toMySQLDatetime(formData.end_date),
        used_count: 0 // Set used_count to 0
      };

      // Use the standard update endpoint
      await api.put(`/coupons/${coupon.id}`, resetData);

      // Update local state
      setUsedCount(0);

      showNotification(
        t('Usage count reset successfully'),
        NotificationType.Success,
        Position.TopRight
      );
      setResetConfirmOpen(false);
      onSuccess(); // Refresh the coupons list
    } catch (err) {
      console.error('Error resetting usage count:', err);
      showNotification(
        t('Error occurred resetting usage count. Please try again later.'),
        NotificationType.Error,
        Position.TopRight
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={coupon ? t('Edit Coupon') : t('Add New Coupon')}
        className="w-full h-auto md:max-w-3xl"
      >
        <div className="flex flex-col h-full">
          <div className="flex-grow overflow-y-auto max-h-[calc(90vh-160px)] pr-2 -mr-4">
            <form id="coupon-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Display used count for existing coupons */}
              {coupon && (
                <div className="flex justify-between items-center mb-4 bg-slate-100 dark:bg-stone-800 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-stone-800 dark:text-zinc-300">
                      {t('Times Used')}: <span className="font-semibold">{usedCount}</span>
                    </span>
                  </div>
                  <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => setResetConfirmOpen(true)}
                    className="flex items-center gap-1 py-1 px-2 text-sm"
                    disabled={usedCount === 0}
                  >
                    <FontAwesomeIcon icon={faRotateLeft} />
                    {t('Reset Usage')}
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
                {/* Coupon Code */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">
                    {t('Coupon Code')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={clsx(
                      'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2',
                      errors.code
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                      'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300'
                    )}
                    placeholder={t('Enter coupon code (e.g. SUMMER50)')}
                  />
                  {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
                </div>

                {/* Discount Type */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">
                    {t('Discount Type')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleChange}
                    className={clsx(
                      'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2',
                      errors.discount_type
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                      'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300'
                    )}
                  >
                    <option value="fixed">{t('Fixed Amount')}</option>
                    <option value="percentage">{t('Percentage')}</option>
                  </select>
                  {errors.discount_type && (
                    <p className="text-red-500 text-sm mt-1">{errors.discount_type}</p>
                  )}
                </div>

                {/* Discount Value */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">
                    {t('Discount Value')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="discount_value"
                      value={emptyInputs.discount_value ? '' : formData.discount_value}
                      onChange={handleChange}
                      className={clsx(
                        'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 w-full',
                        errors.discount_value
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                        'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300',
                        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                      )}
                      placeholder={formData.discount_type === 'percentage' ? '10' : '5.00'}
                    />
                    <span className="absolute right-3 top-2 text-stone-600 dark:text-zinc-400">
                      {formData.discount_type === 'percentage' ? '%' : '€'}
                    </span>
                  </div>
                  {errors.discount_value && (
                    <p className="text-red-500 text-sm mt-1">{errors.discount_value}</p>
                  )}
                </div>

                {/* Minimum Cart Total */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">
                    {t('Minimum Cart Total')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="min_cart_total"
                      value={emptyInputs.min_cart_total ? '' : formData.min_cart_total}
                      onChange={handleChange}
                      className={clsx(
                        'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 w-full',
                        errors.min_cart_total
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                        'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300',
                        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                      )}
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-2 text-stone-600 dark:text-zinc-400">
                      €
                    </span>
                  </div>
                  {errors.min_cart_total && (
                    <p className="text-red-500 text-sm mt-1">{errors.min_cart_total}</p>
                  )}
                </div>

                {/* Start Date */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">
                    {t('Start Date')}
                  </label>
                  <input
                    type="datetime-local"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className={clsx(
                      'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2',
                      errors.start_date
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                      'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300'
                    )}
                  />
                  {errors.start_date && (
                    <p className="text-red-500 text-sm mt-1">{errors.start_date}</p>
                  )}
                </div>

                {/* End Date */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">{t('End Date')}</label>
                  <input
                    type="datetime-local"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className={clsx(
                      'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2',
                      errors.end_date
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                      'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300'
                    )}
                  />
                  {errors.end_date && (
                    <p className="text-red-500 text-sm mt-1">{errors.end_date}</p>
                  )}
                </div>

                {/* Usage Limit */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">
                    {t('Usage Limit')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="usage_limit"
                    value={emptyInputs.usage_limit ? '' : formData.usage_limit}
                    onChange={handleChange}
                    className={clsx(
                      'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2',
                      errors.usage_limit
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                      'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300',
                      '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                    )}
                    placeholder="100"
                  />
                  {errors.usage_limit && (
                    <p className="text-red-500 text-sm mt-1">{errors.usage_limit}</p>
                  )}
                </div>

                {/* Is Active */}
                <div className="flex flex-col">
                  <label className="text-stone-800 dark:text-zinc-300 mb-1">{t('Status')}</label>
                  <div className="flex items-center gap-2 h-[42px]">
                    <label className="relative flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={e => handleChange(e)}
                        name="is_active"
                        className="sr-only peer"
                      />
                      <div
                        className={clsx(
                          'w-9 h-5 peer-focus:outline-0 rounded-full peer transition-all ease-in-out duration-500',
                          "after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all",
                          formData.is_active ? 'peer-checked:bg-green-400' : 'bg-red-500',
                          'peer-checked:after:translate-x-full peer-checked:after:border-transparent'
                        )}
                      ></div>
                    </label>
                    <span
                      className={clsx(
                        'font-semibold',
                        formData.is_active ? 'text-green-500' : 'text-red-500'
                      )}
                    >
                      {formData.is_active ? t('Active') : t('Inactive')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('Active coupons will be visible to users in the promotion banner.')}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="flex flex-col p-1">
                <label className="text-stone-800 dark:text-zinc-300 mb-1">
                  {t('Description')} <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t(
                    'This description will be shown as a smaller text in parentheses below the main offer on the promotion banner.'
                  )}
                </p>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className={clsx(
                    'px-4 py-2 rounded-xl border focus:outline-none focus:ring-2',
                    errors.description
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-slate-300 dark:border-stone-600 focus:ring-orange-500',
                    'bg-white dark:bg-stone-800 text-stone-900 dark:text-zinc-300'
                  )}
                  placeholder={t('E.g., "Halloween special offer" or "New user welcome discount"')}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>
            </form>
          </div>

          {/* Sticky Footer with Buttons */}
          <div className="sticky bottom-0 bg-white dark:bg-stone-900 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end space-x-4">
              <Button variant="cancel" onClick={closeModal} type="button" disabled={submitting}>
                {t('Cancel')}
              </Button>
              <Button variant="primary" type="submit" form="coupon-form" disabled={submitting}>
                {submitting ? t('Saving...') : coupon ? t('Update Coupon') : t('Create Coupon')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Reset Usage Confirmation Modal */}
      <ResetUsageModal
        isOpen={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        onConfirm={handleResetUsedCount}
        isSubmitting={submitting}
      />
    </>
  );
};

export default CouponModal;
